import { formatMemberChartLabel } from '../../../ui/charts/memberActivityUtils';

export function formatBoardUserDisplayName(user) {
  if (!user) return 'Member';
  const first = (user.first_name || '').trim();
  const last = (user.last_name || '').trim();
  if (first && last) return `${first} ${last}`;
  if (first || last) return first || last;
  return (user.username || '').trim() || 'Member';
}

export function formatBoardUserInitials(user) {
  const first = (user?.first_name || '').trim();
  const last = (user?.last_name || '').trim();
  if (first && last) return `${first.charAt(0)}${last.charAt(0)}`.toUpperCase();
  if (first) return first.slice(0, 2).toUpperCase();
  if (last) return last.slice(0, 2).toUpperCase();
  const username = (user?.username || '').trim();
  if (username) return username.slice(0, 2).toUpperCase();
  return 'MB';
}

export function formatBoardShortAuthor(user) {
  return formatMemberChartLabel(user);
}

export function formatBoardRelativeTime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const now = new Date();
  const diffMs = now - d;
  const sec = Math.floor(diffMs / 1000);
  if (sec < 60) return 'just now';
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day === 1) return 'yesterday';
  if (day < 7) return `${day}d ago`;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

/** Announcement cards: absolute local time plus relative suffix. */
export function formatBoardAnnouncementTimestamp(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const absolute = d.toLocaleString(undefined, {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
  const relative = formatBoardRelativeTime(iso);
  return relative ? `${absolute} (${relative})` : absolute;
}

export function normalizeThreadId(id) {
  if (id === null || id === undefined) return '';
  return String(id);
}

export function isThreadExpanded(expandedIds, threadId) {
  const key = normalizeThreadId(threadId);
  return (expandedIds || []).some((id) => normalizeThreadId(id) === key);
}

export function boardReplyCount(thread) {
  const total = Number(thread?.reply_count);
  if (!Number.isFinite(total) || total <= 0) return 0;
  return Math.max(0, total - 1);
}

export function formatBoardApiError(err, fallback = 'Something went wrong') {
  const data = err?.response?.data;
  if (!data) return fallback;
  if (typeof data.error === 'string' && data.error.trim()) return data.error;
  if (typeof data.detail === 'string' && data.detail.trim()) return data.detail;
  if (Array.isArray(data) && data.length > 0) return String(data[0]);
  if (typeof data === 'object') {
    const firstKey = Object.keys(data)[0];
    const val = data[firstKey];
    if (typeof val === 'string') return val;
    if (Array.isArray(val) && val.length > 0) return String(val[0]);
  }
  return fallback;
}

export function stripHtmlToText(html = '') {
  if (!html) return '';
  return String(html)
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/p>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

export function plainTextToBodyHtml(text) {
  const trimmed = (text || '').trim();
  if (!trimmed) return '';
  const escaped = trimmed
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
  return `<p>${escaped}</p>`;
}
